import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEffect } from 'react';

type AppRole = 'admin' | 'user' | 'subscriber';

interface UserRoleData {
  role: AppRole | null;
  isAdmin: boolean;
}

const fetchUserRole = async (): Promise<UserRoleData> => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    return { role: null, isAdmin: false };
  }

  const { data } = await supabase
    .from('user_roles')
    .select('role')
    .eq('user_id', user.id)
    .maybeSingle();

  const role = (data?.role as AppRole) ?? null;
  return {
    role,
    isAdmin: role === 'admin'
  };
};

export const useUserRole = () => {
  const queryClient = useQueryClient();
  
  const query = useQuery({
    queryKey: ['userRole'],
    queryFn: fetchUserRole,
    staleTime: 10 * 60 * 1000,  // Role rarely changes - cache 10 min
    gcTime: 30 * 60 * 1000,
  });

  // Listen for auth changes and invalidate cache
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      () => {
        queryClient.invalidateQueries({ queryKey: ['userRole'] });
      }
    );

    return () => subscription.unsubscribe();
  }, [queryClient]);

  const hasRole = (checkRole: AppRole): boolean => {
    return query.data?.role === checkRole;
  };

  return {
    role: query.data?.role ?? null,
    isAdmin: query.data?.isAdmin ?? false,
    loading: query.isLoading,
    hasRole,
    refresh: query.refetch
  };
};
